import React, { memo, useContext, useEffect, useState, useRef } from 'react'
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose'
import { GiHamburgerMenu } from '@react-icons/all-files/gi/GiHamburgerMenu'
import { MdShoppingBasket } from '@react-icons/all-files/md/MdShoppingBasket'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import AddressOptions from 'components/addressOptions'
import Button from 'components/common/button'
import { useDialog, Container } from 'components/common/modal/Modal'
import SearchBar from 'components/searchbar'
import { RouteConstants } from 'config/routes/routeConstants'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { trackDisplayType } from 'middleware/helper'
import { refreshApp } from 'modules/authentication/actions'
import { setCountryForGlobalAssortment, setAddress } from 'modules/location/actions'
import { getCurrentOrdersList } from 'modules/orders/actions'
import { util } from 'utils'
import { isDeliveryMode, getCurrentCountry, isEmpty, camelCase, orderTypes } from 'utils/appUtils'
import constants from 'utils/constants'
import { getUtmSearch } from 'utils/parse'
import { getCDNURL } from 'network/api/networkSettings'
// import SelectAddress from 'components/selectAddress'
import CustomBasketIcon from '../customBasketIcon'
import { saveCateringAddress, updateCateringAddress, setCateringSchedule, getCateringDataFromStorage } from 'modules/catering/actions'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ModeSwitchPopUp } from '../modeSwitchPopUp'
import ResponsiveDateTimePickers from '../dateTimePicker'

// Extend dayjs with timezone support
dayjs.extend(utc)
dayjs.extend(timezone)

const { DEFAULT_TIMEZONE } = constants;

const MobileHeader = memo(() => {
    const { rtl, strings } = useContext(LanguageContext)
    const applogo = getCDNURL() + 'assets/images/applogo.png'
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { pathname, search } = useLocation()
    const { isSignedIn } = util.getJWTDetails()

    const [modeSwitchOpen, setModeSwitchOpen] = useState(false)
    const [targetMode, setTargetMode] = useState(null)

    const cateringData = useSelector(state => state.catering)
    const currentAddress = useSelector(state => state.location.getIn(['selectedAddress']))
    const addressProcessing = useSelector(state => state.location.getIn(['addressProcessing']))
    const isAddressValid = useSelector(state => state.location.get('uspsValidatedAddressSuccess'))
    const isAddressDeliverable = useSelector(state => state.location.get('isAddressDeliverable'))
    const isLoading = useSelector(state => state.languageChange.get('isLoading'))
    const isCateringMode = useSelector(state => state.appSettings.get('isCateringMode'))
    const displayAddress = currentAddress
        ? currentAddress.get('type') + ': ' + currentAddress.get('formatted_address')
        : isLoading
            ? 'Enter your address'
            : strings.HEADER_ENTER_ADDRESS
    const cateringAddressHead = cateringData?.address?.formatted_address
    // Get store timezone from catering store, fallback to default
    const storeTimezone = cateringData?.store?.time_zone || DEFAULT_TIMEZONE
    const displayAddressHead = currentAddress ? currentAddress.get('formatted_address') : ''
    const displayAddressSub1 = currentAddress ? camelCase(currentAddress.get('type')) + ' ' + strings.from.toLowerCase() : strings.pickupOrDelivery
    const displayAddressSub2 = currentAddress ? currentAddress.get('type') === orderTypes.PICKUP ? strings.at : strings.etaLabel : ''
    const { on, hide, show, getToggleProps, getContainerProps } = useDialog()
    const [singleUseFlag, setSingleUseFlag] = useState(false)
    const cateringSchedule = cateringData?.schedule
    const cateringDateTime = cateringSchedule?.date && cateringSchedule?.time 
        ? `${cateringSchedule.date} ${cateringSchedule.time}` 
        : null
    const [showDateTimePicker, setShowDateTimePicker] = useState(false)
    
    const prevAddressRef = useRef(null)
    const isInitialLoad = useRef(true)
    const prevIsCateringModeRef = useRef(isCateringMode)
    const { openPanel } = useContext(LayoutContext)
    const basketResponse = useSelector(state => state.basket.get('basketResponse'))
    const patchInProgress = useSelector(state => state.basket.get('cartPatchInProgress'))
    const getCartInProgress = useSelector(state => state.basket.get('getCartInProgress'))
    const basketItems = useSelector(state => state.basket.getIn(['basketItems']))
    const totalBasketCount = util.getTotalItems(basketItems)
    const basketCount = totalBasketCount > 0 ? totalBasketCount : ''
    const ETAPrefix = isDeliveryMode()
        ? isLoading
            ? 'ETA:'
            : strings.etaLabel + ' '
        : isLoading
            ? 'Ready At:'
            : strings.readyAtLabel + ' '
    const ETAPresent = basketResponse && basketResponse.toJS().isETAPresent
    const currentStore = useSelector(state => state.location.getIn(['selectedStore']))
    const activeOrdersCount = useSelector(state => state.orders.get('activeOrdersCount'))
    const activeOrder = useSelector(state => state.orders.get('currentOrders')[0])
    const ETAMessage = ETAPresent
        ? basketResponse.toJS().ETA
        : !basketResponse || !currentAddress || !currentStore
            ? ''
            : isDeliveryMode()
                ? isLoading
                    ? 'Drivers not available'
                    : strings.driversNotAvailable
                : ''
    const estimatedTime =
        patchInProgress || getCartInProgress
            ? isLoading
                ? 'Updating...'
                : strings.updating
            : ETAMessage
    const newUserFaceBook = useSelector(state => state.authentication.getIn(['newUserFaceBook']))
    const [state_NewUserFaceBook, setNewUserFaceBook] = useState(
        useSelector(state => state.authentication.getIn(['newUserFaceBook']))
    )
    const isFrom7RewardsView = useSelector(state => state.appSettings.get('isApp'))
    const location = useSelector(state => state.location)
    const user = useSelector(state => state.user)
    const currentPath = useLocation().pathname
    const routes = currentPath.split('/')
    const redirectHome = routes.length > 2 && (routes[1] === 'shop' || 'home')
    const globalAssortmentCountry =
        useSelector(state => state.location.getIn(['countryForGlobalAssortment'])) ||
        getCurrentCountry(location, user)

    // Note: DateTimePicker is now triggered only when user explicitly clicks "Select Store"
    // in ConfirmStore.js, not on address changes

    // Restore catering data from session storage when switching to catering mode
    useEffect(() => {
        const wasCateringMode = prevIsCateringModeRef.current
        prevIsCateringModeRef.current = isCateringMode

        if (isCateringMode) {
            // Entering catering mode - save current shop address and restore catering data
            const currentShopAddress = currentAddress?.toJS?.() || currentAddress
            if (currentShopAddress) {
                sessionStorage.setItem('shop_address_backup', JSON.stringify(currentShopAddress))
            }

            const freshData = getCateringDataFromStorage();
            const { address, schedule, store } = freshData
            dispatch(updateCateringAddress(address, schedule, store))
            return
        }

        if (!wasCateringMode) return

        // Leaving catering mode - restore shop address if it exists
        const shopAddressBackup = sessionStorage.getItem('shop_address_backup')
        if (!shopAddressBackup) return

        try {
            const shopAddress = JSON.parse(shopAddressBackup)
            dispatch(setAddress(shopAddress))
        } catch (error) {
            console.error('Error restoring shop address:', error)
        } finally {
            sessionStorage.removeItem('shop_address_backup')
        }
    }, [isCateringMode]);

    // Handle DateTimePicker changes
    const handleDateTimeChange = (dateTime) => {
        const schedule = {
            date: dayjs(dateTime).format('YYYY-MM-DD'),
            time: dayjs(dateTime).format('HH:mm'),
            isValid: true,
            updatedAt: Date.now()
        };
        dispatch(setCateringSchedule(schedule));
        setShowDateTimePicker(false);
    };

    useEffect(() => {
        if (
            newUserFaceBook &&
            newUserFaceBook.toJS().first_name &&
            newUserFaceBook.toJS().last_name
        ) {
            setNewUserFaceBook(newUserFaceBook.toJS())
            show()
        }
    }, [newUserFaceBook])

    useEffect(() => {
        dispatch(refreshApp())
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(getCurrentOrdersList())
        }, constants.appRefreshTime)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        if (
            !singleUseFlag &&
            addressProcessing &&
            !currentAddress &&
            window.location.search.includes('app=true')
        ) {
            show()
            setSingleUseFlag(true)
        }
    }, [currentAddress, singleUseFlag, addressProcessing])

    useEffect(() => {
        !isFrom7RewardsView && trackDisplayType('Mobile Browser')
    }, [])

    useEffect(() => {
        if (activeOrdersCount === 1) {
            dispatch(getCurrentOrdersList())
        }
    }, [activeOrdersCount])

    const handleSelectCountry = e => {
        dispatch(setCountryForGlobalAssortment(e.target.value))
        if (redirectHome) navigate('/home')
    }

    return (
        <>
            <div className="header-mobile">
                <section className="address_section">
                    <div className="address_section_address">
                        <button
                            className="address"
                            {...getToggleProps()}
                            aria-label="delivery/pickup address"
                        >
                            {isCateringMode ? (
                                <button
                                    className={`header__desktop__address header__desktop__address__catering`}
                                    {...getToggleProps()}
                                    aria-label="delivery/pickup address"
                                >
                                    {cateringData?.address ? (
                                        <div className='header__desktop__address__store_details'>
                                            <div className="header__desktop__address__first_line">
                                                <span
                                                    className="header__desktop__address__non_clickable"
                                                    aria-label="Pickup from"
                                                >
                                                    {strings.pickupFrom}&nbsp;
                                                </span>
                                                <span
                                                    className="header__desktop__address__clickable"
                                                    tabIndex="0"
                                                    aria-label={cateringAddressHead}
                                                >
                                                    {cateringAddressHead}
                                                </span>
                                            </div>
                                            {cateringData?.schedule && (
                                                <div className="header__desktop__address__second_line">
                                                    <span
                                                        className="header__desktop__address__non_clickable"
                                                        aria-label="at"
                                                    >
                                                        at&nbsp;
                                                    </span>
                                                    <span
                                                        className="header__desktop__address__clickable"
                                                        tabIndex="0"
                                                        aria-label={dayjs(cateringData?.schedule?.date + ' ' + cateringData?.schedule?.time).format('MMM DD, h:mm A')}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent address modal from opening
                                                            setShowDateTimePicker(true);
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {dayjs(cateringData?.schedule?.date + ' ' + cateringData?.schedule?.time).format('MMM DD, h:mm A')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='header__desktop__address__store_details'>
                                            <span
                                                className="header__desktop__address__store_details_subHead"
                                                tabIndex="0"
                                                aria-label={''}
                                            >
                                                {strings.startCateringOrder}
                                            </span>
                                            <span className={`header__desktop__address__store_details_head`}>
                                                {strings.findAStore}
                                            </span>
                                        </div>
                                    )}
                                </button>
                            ) : (
                                <>
                                    <div className='header__desktop__address__store_details'>
                                        <span
                                            className="header__desktop__address__store_details_subHead"
                                            tabIndex="0"
                                            aria-label={displayAddressSub1}
                                        >
                                            {displayAddressSub1}
                                        </span>
                                        <span className={`header__desktop__address__store_details_head`}>
                                            {displayAddressHead}
                                        </span>
                                    </div>

                                    {on && (
                                        <Container {...getContainerProps()}>
                                            <div className="signinform">
                                                {!isFrom7RewardsView ? (
                                                    <Button
                                                        {...getToggleProps()}
                                                        aria-label="close modal"
                                                        className="closeModal"
                                                    >
                                                        <AiOutlineClose className="close" size="2rem" />
                                                    </Button>
                                                ) : null}
                                                <AddressOptions hide={hide} show={show} setShowDateTimePicker={setShowDateTimePicker} />
                                                {/* <SelectAddress hide={hide} show={show} /> */}
                                            </div>
                                        </Container>
                                    )}
                                    <div className='header__desktop__address__eta_details'>
                                        {displayAddressSub2 && <span
                                            className="header__desktop__address__eta_details_subHead"
                                            tabIndex="0"
                                            aria-label={displayAddressSub2}
                                        >
                                            {displayAddressSub2}
                                        </span>}
                                        <span className={`header__desktop__address__eta_details_head`}>
                                            {currentAddress ? estimatedTime : strings.findAStore}
                                        </span>
                                    </div>
                                </>
                            )}
                        </button>

                        {/* Address modal for catering address */}
                        {isCateringMode && on && (
                            <Container {...getContainerProps()}>
                                <div className="signinform">
                                    <Button
                                        {...getToggleProps()}
                                        aria-label="close modal"
                                        className="closeModal"
                                    >
                                        <AiOutlineClose className="close" size="2.5rem" />
                                    </Button>
                                    <AddressOptions
                                        hide={hide}
                                        show={show}
                                        setShowDateTimePicker={setShowDateTimePicker}
                                    />
                                    {/* <SelectAddress hide={hide} show={show} /> */}
                                </div>
                            </Container>
                        )}

                        {/* DateTimePicker modal for catering */}
                        {isCateringMode && cateringData?.address && (
                            <ResponsiveDateTimePickers
                                open={showDateTimePicker}
                                onClose={() => setShowDateTimePicker(false)}
                                onDateTimeChange={handleDateTimeChange}
                                initialValue={cateringDateTime}
                                storeTimezone={storeTimezone}
                            />
                        )}
                    </div>
                    <div className="flex-space-between-item">
                        {state_NewUserFaceBook && state_NewUserFaceBook.first_name && (
                            <div>
                                {on && (
                                    <Container {...getContainerProps()}>
                                        <div className="registerform">
                                            <Button {...getToggleProps()} aria-label="close modal">
                                                <AiOutlineClose className="close" size="2.5rem" />
                                            </Button>
                                            <AddressOptions hide={hide} show={show} registration setShowDateTimePicker={setShowDateTimePicker} />
                                            {/* <SelectAddress hide={hide} show={show} registration /> */}
                                        </div>
                                    </Container>
                                )}
                            </div>
                        )}

                        {isSignedIn ? (
                            <button
                                className={`profile-image ${isSignedIn ? 'alignLeft' : null}`}
                                onClick={() => {
                                    openPanel('accountPanel')
                                    navigate(RouteConstants.DrawerAccount)
                                }}
                                aria-label={'profile icon, enter to open your account information'}
                            />
                        ) : isFrom7RewardsView ? null : (
                            <button
                                className="hamburger"
                                onClick={() => openPanel('signInPanel')}
                                aria-label={'click to open the signin/register options'}
                            >
                                <GiHamburgerMenu className="hamburger-icon" />
                            </button>
                        )}
                    </div>
                </section>
            </div>
            <section className={'search_section'}>
                <div className="search_section_searchbar">
                    <SearchBar className='searchbar' />
                </div>
                <CustomBasketIcon count={basketCount} />
            </section>
            {isAddressValid && isEmpty(currentAddress) && isAddressDeliverable === false && (
                <div className="address-feedback">
                    {strings.outOfServiceArea} <a {...getToggleProps()}>{strings.hereLabel}</a>.
                </div>
            )}
            {pathname.indexOf(RouteConstants.OrderStatus) === -1 && activeOrdersCount > 0 ? (
                <>
                    <div
                        className="order-feedback"
                        tabIndex="0"
                        aria-label={`${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount3}`}
                    >
                        {activeOrdersCount === 1
                            ? `${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount2}`
                            : `${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount3}`}{' '}
                        {activeOrdersCount === 1 ? (
                            <Link
                                to={`${RouteConstants.OrderStatus}${getUtmSearch(search)}`}
                                state={{ order: activeOrder }}
                                className="hereLink"
                            >
                                {strings.hereLabel}
                            </Link>
                        ) : (
                            <button
                                className={'here'}
                                onClick={() => openPanel('orderPanel')}
                                tabIndex="0"
                            >
                                {strings.hereLabel}
                            </button>
                        )}
                        .
                    </div>
                    {isFrom7RewardsView && (
                        <img
                            className={'order-feedback-loader'}
                            src={getCDNURL() + 'assets/gifs/progress.svg'}
                            alt={strings.loading}
                        />
                    )}
                </>
            ) : null}
            <ModeSwitchPopUp
                open={modeSwitchOpen}
                onClose={() => setModeSwitchOpen(false)}
                targetMode={targetMode}
                search={search}
            />
        </>
    )
})

export default MobileHeader
